import type { SnackBarConfig } from '@shared/components/Snackbar';
import type { IntelligenceRequest } from '@shared/domain/intelligenceRequest/IntelligenceRequest';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import SentimentSatisfiedAltOutlinedIcon from '@mui/icons-material/SentimentSatisfiedAltOutlined';
import { Box, Chip, DialogActions, FormControl, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import Button from '@shared/components/Buttons/Button';
import { IconName } from '@shared/components/Icon';
import InfoBanner from '@shared/components/InfoBanner';
import CustomSnackbar from '@shared/components/Snackbar';
import { IntelligenceRequestStatus } from '@shared/domain/intelligenceRequest/IntelligenceRequestStatus';
import { useApi } from '@shared/services/api';
import { HelpText, StepContent } from '@shared/styles/ModalStyles';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useState } from 'react';
import { useMessageService } from '@/shared/services/MessageService/MessageServiceContext';

enum DecisionOption {
  ACCEPTED = 'accepted',
  DECLINED = 'declined',
}

enum ModalStep {
  DECIDE = 'decide',
  DECLINED = 'declined',
  ACCEPTED = 'accepted',
}

interface ViewConsultationRequestModalProps {
  intelligenceRequest: IntelligenceRequest;
  onRefetch?: () => Promise<void> | void;
}

const ViewConsultationRequestModal: React.FC<ViewConsultationRequestModalProps> = ({
  intelligenceRequest,
  onRefetch,
}) => {
  const api = useApi();
  const [selectedDecision, setSelectedDecision] = useState<DecisionOption | ''>('');
  const [loading, setLoading] = useState<'submit' | null>(null);
  const [decision, setDecision] = useState<DecisionOption | null>(null);
  const [snackbarConfig, setSnackbarConfig] = useState<SnackBarConfig>();
  const [step, setStep] = useState<ModalStep>(ModalStep.DECIDE);

  const { closeMessage } = useMessageService();

  const handleSnackBarClose = () => {
    if (!snackbarConfig) return;
    setSnackbarConfig({ ...snackbarConfig, open: false });
  };

  const refetchIRs = async () => {
    if (onRefetch) {
      await onRefetch();
    }
  };

  const handleClose = async () => {
    try {
      await refetchIRs();
    } finally {
      closeMessage();
      setSelectedDecision('');
      setDecision(null);
      setStep(ModalStep.DECIDE);
      setSnackbarConfig(undefined);
      setLoading(null);
    }
  };

  const issuerName = intelligenceRequest.user
    ? `${intelligenceRequest.user.firstName} ${intelligenceRequest.user.lastName}`
    : 'Unknown User';

  const amountLabel =
    typeof intelligenceRequest.amountUsd === 'number'
      ? new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0,
        }).format(intelligenceRequest.amountUsd)
      : undefined;

  const submitDecision = async () => {
    if (!intelligenceRequest.id || !selectedDecision) return;
    setLoading('submit');
    try {
      if (selectedDecision === DecisionOption.DECLINED) {
        const payload = {
          ...intelligenceRequest,
          status: IntelligenceRequestStatus.REJECTED,
        };
        await api.patch(`/intelligence-request/${intelligenceRequest.id}`, payload);
        await refetchIRs();
        setDecision(DecisionOption.DECLINED);
        setStep(ModalStep.DECLINED);
        setSnackbarConfig({
          open: true,
          type: 'success',
          message: 'Request declined.',
        });
      } else {
        const payload = {
          ...intelligenceRequest,
          status: IntelligenceRequestStatus.ACCEPTED_BY_REVIEW_AUTHOR,
        };
        await api.patch(`/intelligence-request/${intelligenceRequest.id}/accept-by-review-author`, payload);
        await refetchIRs();
        setDecision(DecisionOption.ACCEPTED);
        setStep(ModalStep.ACCEPTED);
        setSnackbarConfig({
          open: true,
          type: 'success',
          message: 'Request accepted.',
        });
      }
    } catch {
      setSnackbarConfig({
        open: true,
        type: 'error',
        message: 'Failed to update request. Please try again.',
      });
    } finally {
      setLoading(null);
    }
  };

  const formatEngagementType = (type: string) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'info' | 'error' | 'default' => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
      case 'ACCEPTED_BY_REVIEW_AUTHOR':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'FULFILLED':
        return 'info';
      case 'CANCELLED':
      case 'REJECTED':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatStatus = (status: string) => {
    return status
      .replace(/_/g, ' ')
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 860,
      }}
    >
      <StepContent>
        {step === ModalStep.DECIDE && (
          <>
            <InfoBanner
              title="Private Consultation Request"
              message="This request is private. Your response will be sent to the requester and stored against the request."
              iconName={IconName.Lock}
            />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Box
                sx={{
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  borderRadius: 3,
                  bgcolor: 'background.paper',
                  p: 2,
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Request Details
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Requester
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {issuerName}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Description
                    </Typography>
                    <HelpText variant="body2">
                      {intelligenceRequest.description ||
                        'The requester has requested a private consultation with you.'}
                    </HelpText>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Engagement Type
                      </Typography>
                      <Typography variant="body2">
                        {formatEngagementType(intelligenceRequest.engagementType)}
                      </Typography>
                    </Box>

                    {amountLabel && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          Offered Amount
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <MonetizationOnOutlinedIcon sx={{ fontSize: 16, color: 'success.main' }} />
                          <Typography variant="body2" fontWeight={600} color="success.main">
                            {amountLabel}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Status
                      </Typography>
                      <Box>
                        <Chip
                          label={formatStatus(intelligenceRequest.status)}
                          color={getStatusColor(intelligenceRequest.status)}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>
                    </Box>
                  </Box>

                  {intelligenceRequest.createdAt && (
                    <Box mt={0.5}>
                      <HelpText variant="caption">
                        Requested on {new Date(intelligenceRequest.createdAt).toLocaleDateString()}
                      </HelpText>
                    </Box>
                  )}
                </Box>
              </Box>

              {intelligenceRequest.status === IntelligenceRequestStatus.PENDING && (
                <Box
                  sx={{
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    borderRadius: 3,
                    bgcolor: 'background.paper',
                    p: 2,
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    Decision
                  </Typography>
                  <HelpText variant="body2" gutterBottom>
                    Choose whether you want to accept or decline this consultation request, then submit.
                  </HelpText>
                  <FormControl>
                    <RadioGroup
                      row
                      name="ir-decision"
                      value={selectedDecision}
                      onChange={(_, v) => setSelectedDecision((v as DecisionOption) || '')}
                    >
                      <FormControlLabel value="accepted" control={<Radio />} label="Accept" />
                      <FormControlLabel value="declined" control={<Radio />} label="Decline" />
                    </RadioGroup>
                  </FormControl>
                </Box>
              )}
            </Box>

            <AnimatePresence>
              {decision === DecisionOption.ACCEPTED && (
                <Box
                  component={motion.div}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    color: '#1b5e20',
                  }}
                >
                  <CheckCircleIcon />
                  <Typography variant="body1" fontWeight={600}>
                    You accepted the request. We'll notify the requester.
                  </Typography>
                </Box>
              )}
              {decision === DecisionOption.DECLINED && (
                <Box
                  component={motion.div}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    color: '#9c1a1a',
                  }}
                >
                  <HighlightOffIcon />
                  <Typography variant="body1" fontWeight={600}>
                    You declined the request. Thanks for letting us know.
                  </Typography>
                </Box>
              )}
            </AnimatePresence>
          </>
        )}

        {step === ModalStep.DECLINED && (
          <Box
            sx={{
              border: '1px solid rgba(0, 0, 0, 0.08)',
              borderRadius: 3,
              bgcolor: 'background.paper',
              p: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                minHeight: 220,
                gap: 1.5,
                p: { xs: '12px', md: '24px 12px' },
              }}
            >
              <SentimentSatisfiedAltOutlinedIcon
                sx={{
                  fontSize: '48px !important',
                  lineHeight: 1,
                  color: '#5f6b7a',
                }}
                aria-label="declined"
              />
              <Typography variant="h6" fontWeight={700}>
                Request declined
              </Typography>
              <HelpText variant="body1">We've notified the requester. Thanks for taking a look!</HelpText>
            </Box>
          </Box>
        )}

        {step === ModalStep.ACCEPTED && (
          <Box
            sx={{
              border: '1px solid rgba(0, 0, 0, 0.08)',
              borderRadius: 3,
              bgcolor: 'background.paper',
              p: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                minHeight: 220,
                gap: 1.5,
                p: { xs: '12px', md: '24px 12px' },
              }}
            >
              <HandshakeOutlinedIcon
                sx={{
                  fontSize: '48px !important',
                  lineHeight: 1,
                  color: '#2e7d32',
                }}
                aria-label="accepted"
              />
              <Typography variant="h6" fontWeight={700}>
                Request accepted
              </Typography>
              <HelpText variant="body1">
                A Kandir team member will reach out shortly to coordinate next steps. We'll:
              </HelpText>
              <HelpText variant="body2">
                • Confirm the scope of the conversation and any guardrails
                <br />
                • Propose times based on your availability and preferred channel (chat or call)
                <br />• Share any required compliance, privacy, or payment details
              </HelpText>
              <HelpText variant="body2">
                You'll receive an email and an in‑app notification with the details. If anything changes, you can reply
                to the message to adjust timing or ask questions.
              </HelpText>
            </Box>
          </Box>
        )}
      </StepContent>

      <DialogActions
        sx={{
          justifyContent: 'space-between',
          p: 2,
        }}
      >
        <Button size="small" onClick={handleClose} disabled={loading !== null}>
          Close
        </Button>
        {step === ModalStep.DECIDE && intelligenceRequest.status === IntelligenceRequestStatus.PENDING && (
          <Button
            size="small"
            color="primary"
            onClick={submitDecision}
            disabled={loading !== null || !!decision || !selectedDecision}
          >
            {loading === 'submit' ? 'Submitting…' : 'Submit'}
          </Button>
        )}
      </DialogActions>

      <CustomSnackbar config={snackbarConfig} setOpen={handleSnackBarClose} />
    </Box>
  );
};

export default ViewConsultationRequestModal;
